import { useCallback, useEffect, useMemo, useReducer } from 'react';
import {
  createScoringState,
  currentValue,
  ScoreLevel,
  scoringReducer,
  ScoringState,
  Verdict,
} from '../utils/scoring';

/** Restores a saved counter, falling back to a fresh one on missing or malformed data. */
const loadState = (storageKey?: string): ScoringState => {
  const fresh = createScoringState();
  if (!storageKey) return fresh;
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
    if (!saved || typeof saved !== 'object' || typeof saved.players !== 'object') return fresh;
    return { ...fresh, ...saved, past: Array.isArray(saved.past) ? saved.past : [] };
  } catch {
    return fresh;
  }
};

/**
 * React binding for the point counter in utils/scoring.ts. Holds no buzzer
 * knowledge — callers decide who gets judged and what happens afterwards.
 * With a `storageKey` the counter (undo history included) survives reloads.
 */
export const useScoring = (storageKey?: string) => {
  const [state, dispatch] = useReducer(scoringReducer, storageKey, loadState);

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // storage unavailable (private mode) — the counter just won't survive a reload
    }
  }, [state, storageKey]);

  const judge = useCallback(
    (playerName: string, verdict: Verdict) => dispatch({ type: 'judge', playerName, verdict }),
    []
  );
  const skipQuestion = useCallback(() => dispatch({ type: 'skipQuestion' }), []);
  const setQuestion = useCallback((questionNo: number) => dispatch({ type: 'setQuestion', questionNo }), []);
  const setLevel = useCallback((level: ScoreLevel) => dispatch({ type: 'setLevel', level }), []);
  const setLevelLocked = useCallback((locked: boolean) => dispatch({ type: 'setLevelLocked', locked }), []);
  const adjust = useCallback(
    (playerName: string, points: number) => dispatch({ type: 'adjust', playerName, points }),
    []
  );
  const removePlayer = useCallback((playerName: string) => dispatch({ type: 'removePlayer', playerName }), []);
  const resetScores = useCallback(() => dispatch({ type: 'resetScores' }), []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);

  const value = useMemo(() => currentValue(state), [state]);

  return {
    state,
    value,
    canUndo: state.past.length > 0,
    judge,
    skipQuestion,
    setQuestion,
    setLevel,
    setLevelLocked,
    adjust,
    removePlayer,
    resetScores,
    undo,
  };
};

export type ScoringController = ReturnType<typeof useScoring>;
